/* @flow */

import type { CARD_DATA_MODAL_TYPE } from '../../components/modal/cardModal/CardModalConstsAndTypes';
import type { ChannelMap } from '../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_SCHEDULE_LOCATION } from '../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_AUTHENT_REALM } from '../../libs/netgemLibrary/v8/types/Realm';
import type { KeyValuePair } from '../jsHelpers/types';

import { ItemContent } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_API_V8_RIGHTS, NETGEM_API_V8_RIGHTS_PLAYBACK } from '../../libs/netgemLibrary/v8/types/Rights';
import { getChannelRights } from '../channel/helper';
import { logError } from '../debug/debug';
import { Messenger, MessengerEvents } from '../messenger';
import { FAKE_EPG_LIVE_PREFIX } from '../ui/item/types';
import { type PENDING_OPERATION_DATA_TYPE, PendingOperationKind, PendingOperationReason, storePendingOperation } from './pendingOperations';
import { getRequiredRights, isAccessGranted, showMissingSubscription } from './rights';

const isChannelPlaybackGranted: (isSubscriptionFeatureEnabled: boolean, channelRights: ?NETGEM_API_V8_RIGHTS, defaultRights: ?NETGEM_API_V8_RIGHTS, userRights: Array<string> | null) => boolean = (
  isSubscriptionFeatureEnabled,
  channelRights,
  defaultRights,
  userRights,
) => {
  if (!isSubscriptionFeatureEnabled) {
    // Subscription feature not enabled: no restriction
    return true;
  }

  const requiredRights = getRequiredRights(NETGEM_API_V8_RIGHTS_PLAYBACK, null, channelRights, defaultRights);
  return isAccessGranted(requiredRights, userRights);
};

const isPlaybackGranted: (
  isRegisteredAsGuest: boolean,
  isSubscriptionFeatureEnabled: boolean,
  cardData: CARD_DATA_MODAL_TYPE,
  authority?: NETGEM_API_V8_AUTHENT_REALM,
  contentType: ItemContent,
  location: ?NETGEM_API_V8_METADATA_SCHEDULE_LOCATION,
  channels: ChannelMap,
  commercialOffers: ?KeyValuePair<Array<string>>,
  defaultRights: ?NETGEM_API_V8_RIGHTS,
  userRights: Array<string> | null,
) => boolean = (isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, authority, contentType, location, channels, commercialOffers, defaultRights, userRights) => {
  const data: CARD_DATA_MODAL_TYPE | PENDING_OPERATION_DATA_TYPE = {
    ...cardData,
    pendingOperation: {
      authority,
      contentType,
      operationType: PendingOperationKind.Play,
      reason: PendingOperationReason.RequireAccount,
    },
  };

  const {
    item: { selectedLocation, selectedProgramId },
  } = cardData;

  if (!location && !selectedProgramId.startsWith(FAKE_EPG_LIVE_PREFIX)) {
    // Location is null
    logError('Playback error: no location');
    return false;
  }

  if (isRegisteredAsGuest) {
    // Guest mode: let's open the sign-in page
    storePendingOperation(data);
    Messenger.emit(MessengerEvents.SHOW_SIGN_IN, PendingOperationKind.Play);
    return false;
  }

  if (!isSubscriptionFeatureEnabled) {
    // Subscription feature not enabled: no restriction
    return true;
  }

  const channelId = location?.channelId ?? selectedLocation.channelId;
  const channelRights = getChannelRights(channels, channelId);
  const requiredRights = getRequiredRights(NETGEM_API_V8_RIGHTS_PLAYBACK, location, channelRights, defaultRights);
  if (isAccessGranted(requiredRights, userRights)) {
    // User has required rights
    return true;
  }

  if (!data.item) {
    return false;
  }

  // User rights do not meet required rights: open subscribe page and highlight matching commercial offer
  showMissingSubscription(data as CARD_DATA_MODAL_TYPE, requiredRights, commercialOffers);
  return false;
};

export { isChannelPlaybackGranted, isPlaybackGranted };
